import React, { Dispatch, SetStateAction } from 'react'
import i18n from 'live/services/i18n'
import styled from 'styled-components'
import UsersSelect from 'live/components/forms/ui/users-select'
import LiveSelect from 'live/components/forms/ui/select'
import { cloneDeep } from 'lodash'
import { emptyOperatorData } from './lineConfig'
import { AddRowButton, RowRemoveButton } from '../common/components'
import { userFilter } from '../common/utils'

interface IProps {
    lineOperators: LineOperators[]
    setLineOperators: Dispatch<SetStateAction<LineOperators[]>>
}
export function UserTable({ lineOperators, setLineOperators }: IProps): JSX.Element {
    function handleOnChange(type: keyof LineOperators, index: number, value): void {
        const newConfigs = cloneDeep(lineOperators)
        newConfigs[index][type] = value
        setLineOperators(newConfigs)
    }

    function onRemoveOperator(index): void {
        setLineOperators(lineOperators.filter((operator, operatorIndex) => index !== operatorIndex))
    }

    return (
        <StyledUserTable>
            <thead>
                <tr>
                    <th>{i18n('operator')}</th>
                    <th>{i18n('line type')}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {lineOperators.map((user, index) => (
                    <tr key={index}>
                        <td>
                            <UsersSelect
                                minCharQuery="1"
                                onChange={userData => {
                                    const { value, label } = userData
                                    handleOnChange('userData', index, userData ? { value, label } : null)
                                }}
                                selected={user.userData}
                                filter={user =>
                                    userFilter(
                                        user,
                                        lineOperators.map(el => el.userData)
                                    )
                                }
                            />
                        </td>
                        <td className="lineConfig--userTable__lineType">
                            <LiveSelect
                                name="lineType"
                                value={user.operatorType}
                                options={[
                                    { value: 'primary', label: i18n('primary') },
                                    { value: 'secondary', label: i18n('secondary') }
                                ]}
                                onChange={value => handleOnChange('operatorType', index, value)}
                                className="lineConfig--userTable__lineType--lineTypeSelect"
                                clearable={false}
                                searchable={false}
                                placeholder={i18n('select operator type')}
                            ></LiveSelect>
                        </td>
                        <td className="lineConfig--userTable__remove">
                            <RowRemoveButton onClick={() => onRemoveOperator(index)}>
                                <i className="far fa-trash-alt" />
                            </RowRemoveButton>
                        </td>
                    </tr>
                ))}
                <tr>
                    <td colSpan={3} className="addButton">
                        <AddRowButton onClick={() => setLineOperators([...lineOperators, emptyOperatorData()])}>
                            <i className="fas fa-plus" /> {`${i18n('add')} ${i18n('operator')}`}
                        </AddRowButton>
                    </td>
                </tr>
            </tbody>
        </StyledUserTable>
    )
}

const StyledUserTable = styled.table`
    --border: 1px solid ${({ theme }) => theme['gray-400']};
    border-radius: 3px;
    width: 100%;
    border-collapse: separate;
    .Select {
        &-control {
            border: none;
        }
    }
    .live-select {
        &.form-group {
            margin: 0;
        }
    }
    tr {
        td {
            border: var(--border);
            border-top: none;
            &:first-child,
            &:nth-child(2) {
                border-right: none;
            }
            &.addButton {
                border-right: var(--border);
            }
        }
        &:last-child {
            td {
                &:first-child {
                    border-bottom-left-radius: 3px;
                }
                &:last-child {
                    border-bottom-right-radius: 3px;
                }
            }
        }
    }
    th {
        width: 50%;
        border: var(--border);
        background-color: ${({ theme }) => theme['gray-100']};
        color: ${({ theme }) => theme['gray-800']};
        padding: 8px 16px;
        text-transform: capitalize;
        &:first-child {
            border-right: none;
            border-top-left-radius: 3px;
        }
        &:nth-child(2) {
            border-right: none;
        }
        &:last-child {
            border-top-right-radius: 3px;
            border-left: none;
        }
    }
`
